import React, { useMemo } from 'react';
import Link from 'next/link';
import { SymbolIcon } from './icon/symbol-icon';
import { SymbolIconEtf } from './icon/etf/symbol-icon-etf';
import { IconSymbolResponsiveSizeType, IconSymbolSizeType } from '../../../theme/type/icon-symbol-type';
import SkeletonIconSymbol from '../skeleton/icon/symbol/skeleton-icon-symbol';
import { SymbolTypeEnum } from '../../../defs/api';
import { ValueString } from '../value/string/value-string';

type SymbolCommonType = {
    skeleton?: boolean;
    size?: IconSymbolSizeType | IconSymbolResponsiveSizeType | undefined;
};

type SymbolSkeletonType = {
    skeleton: true;
    name?: React.ReactNode;
    description?: React.ReactNode;
    value?: React.ReactNode;
    ticker?: React.ReactNode;
    locked?: boolean;
};

type SymbolDefaultType = {
    skeleton?: false;
    name?: string;
    symbol: string | undefined;
    onClick?: () => void;
    description?: React.ReactNode | string | null;
    value?: string | null;
    type?: SymbolTypeEnum | null;
    url?: string;
    ticker?: string;
    locked?: boolean;
};

type SymbolType = (SymbolSkeletonType & SymbolCommonType) | (SymbolDefaultType & SymbolCommonType);

export const Symbol: React.FC<SymbolType> = (props) => {
    const { skeleton, size, name, description, value, ticker, locked = false } = props;

    if (skeleton) {
        return (
            <div className="inline-flex items-center gap-vsmall">
                <SkeletonIconSymbol size={size} />
                {(name || ticker || description) && (
                    <div className="flex flex-col">
                        {(name || ticker) && (
                            <span className="font-bold text-brand-secondary transition-colors">
                                {name}{' '}
                                {ticker && (
                                    <span className="font-medium text-brand-secondary/60 transition-colors">
                                        &mdash; {ticker}
                                    </span>
                                )}
                            </span>
                        )}
                        {description && (
                            <span className="font-medium text-brand-secondary/60 transition-colors">{description}</span>
                        )}
                    </div>
                )}
                {value && <div className="ml-vsmall font-medium text-brand-secondary/60">{value}</div>}
            </div>
        );
    }

    const { symbol, onClick, type, url } = props;

    const symbolIcon = useMemo(() => {
        if (locked) {
            return <SkeletonIconSymbol size={'medium'} />;
        }

        if (type === SymbolTypeEnum.Et) {
            return <SymbolIconEtf size={size} />;
        }

        return <SymbolIcon symbol={symbol} shadow={false} size={size} type={type} />;
    }, [symbol, type]);

    const symbolContent = (
        <div onClick={onClick} className="inline-flex items-center gap-vsmall">
            <span className="flex rounded-small transition-[box-shadow] group-hover:shadow-large">{symbolIcon}</span>
            {(name || ticker || description) && (
                <div className="flex flex-col">
                    {(name || ticker) && (
                        <span className="font-bold text-brand-secondary transition-colors group-hover:text-brand-secondary-lighten">
                            <ValueString text={name || ''} locked={locked} mode="pure" />
                            {ticker && (
                                <span className="font-medium text-brand-secondary/60 transition-colors group-hover:text-brand-secondary">
                                    &mdash; <ValueString text={ticker} locked={locked} mode="pure" />
                                </span>
                            )}
                        </span>
                    )}
                    {description && (
                        <span className="font-medium text-brand-secondary/60 transition-colors group-hover:text-brand-secondary">
                            {description}
                        </span>
                    )}
                </div>
            )}
            {value && (
                <div className="ml-vsmall font-medium text-brand-secondary/60">
                    <ValueString text={value} locked={locked} mode="pure" />
                </div>
            )}
        </div>
    );

    if (url) {
        if (locked) {
            return <a className="group">{symbolContent}</a>;
        }

        return (
            <Link href={url} passHref>
                <a className="group">{symbolContent}</a>
            </Link>
        );
    }

    return symbolContent;
};
